import * as KlipApi from '@/api/klip-api.proxy';
import { useUserStore } from './UserDataModule';
import { useErrorStore } from '@/stores/error-store';
import {klipApiProxy} from '@/plugins/proxy-client';
import {defineStore} from 'pinia';
import {computed, ref, Ref} from 'vue';
import {ShoppingCartMapRequest, ShoppingCartStatus} from "@/api/klip-api.proxy";
import {useRouter} from '@/router/router-vue3';

export interface IShoppingCardItem {
    //id: string | number;
    origPaId?: string; // for PA
    origImklId?: number; // for IMKL
    linkedMaprequestId?: string; // for IMKL
    isIndependent?: boolean; // for IMKL
    type: 'PA' | 'IMKL';
    // delete: boolean; // ???
    error: boolean;
    reference: string;
    price: number;
    priceLabel: string;
    isAlreadyPaid?: boolean; // for IMKL
    isExpired?: boolean; // for PA
    organisation: string;
    dateReceived?: string;
}

export const useShoppingCartStore = defineStore('shopping-cart-store', () => {

    const router = useRouter();

    const items: Ref<IShoppingCardItem[]> = ref([]);
    const totalAmount: Ref<number> = ref(0);
    const totalPriceInclVatFormatted: Ref<string> = ref('');
    const totalPriceExclVatFormatted: Ref<string> = ref('');
    const totalVatFormatted: Ref<string> = ref('');
    const vatPercentage: Ref<number | undefined> = ref();
    const itemToBeRemoved: Ref<IShoppingCardItem> = ref(null);
    const status: Ref<string> = ref('');
    const paymentLink: Ref<string> = ref('');


    const numberOfItems = computed((): number => {
        return items.value.length;
    });

    const shoppingCartItems = computed((): IShoppingCardItem[] => {
        return items.value;
    });

    const shoppingCartTotalPriceExclVat = computed((): string => {
        return `€ ${totalPriceExclVatFormatted.value}`;
    });

    const shoppingCartTotalPriceInclVat = computed((): string => {
        return `€ ${totalPriceInclVatFormatted.value}`;
    });

    const shoppingCartTotalVat = computed((): string => {
        return `€ ${totalVatFormatted.value}`;
    });

    const shoppingVatPercentage = computed((): string => {
        return `${vatPercentage.value}%`;
    });

    const getTotalAmount = computed((): number => {
        return totalAmount.value;
    });

    const isShoppingCartActive = computed((): boolean => {
        if (!!useUserStore().user && useUserStore().user.roles) {
            return useUserStore().user.roles.includes('mri') && useUserStore().user.isShoppingCartVisible;
        }
        return false;
    })

    const shoppingCartStatus = computed((): string => {
        return status.value;
    });

    const shoppingCartPaymentLink = computed((): string => {
        return paymentLink.value;
    });

    const numberOfMapRequests = computed((): number => {
        return items.value.filter((item: IShoppingCardItem) => item.type === 'PA').length;
    });

    const hasMapRequestExtras = computed((): boolean => {
        return items.value.filter((item: IShoppingCardItem) => item.isIndependent === true).length > 0;
    });

    const hasMapRequestsWithImkl = computed((): boolean => {
        return items.value.filter((item: IShoppingCardItem) => item.isIndependent === false).length > 0;
    });

    const hasInvalidItems = computed((): boolean => {
        return items.value.filter((item: IShoppingCardItem) => item.error === true).length > 0;
    });


    const _setShoppingCartItems = (bffItems: ShoppingCartMapRequest[]) => {
        if (!bffItems) {
            items.value = [];
            return;
        }

        items.value = bffItems.map(m => {
            const item: IShoppingCardItem = {
                //id: m.id,
                origPaId: m.id,
                type: 'PA',
                dateReceived: m.dateReceived,
                isExpired: m.isExpired,
                price: m.price,
                reference: `Planaanvraag: ${m.reference}`,
                priceLabel: `€ ${useUserStore().applyVat ? m.priceFormatted : m.price}`,
                //delete: true,
                error: m.isExpired,
                organisation: m.organisation,
                isIndependent: true,
            };
            return item;
        });

        // sort on date
        items.value = items.value.sort((a: IShoppingCardItem, b: IShoppingCardItem) => Date.parse(a.dateReceived) - Date.parse(b.dateReceived));
    }

    const _addExtraShoppingCartItems = (value: KlipApi.ShoppingCartMapRequestExtra[]) => {
        const createImklItem = (extra: KlipApi.ShoppingCartMapRequestExtra, isIndependent: boolean): IShoppingCardItem => {
            return {
                type: 'IMKL',
                origImklId: extra.id,
                //delete: true,
                reference: `IMKL: ${extra.reference}`,
                price: extra.price,
                priceLabel: `€ ${useUserStore().applyVat ? extra.priceFormatted : extra.price}`,
                linkedMaprequestId: extra.mapRequestId,
                isIndependent,
                isAlreadyPaid: extra.isAlreadyPaid,
                error: extra.isAlreadyPaid,
                organisation: extra.organisation,
            };
        };

        value.forEach((extra: KlipApi.ShoppingCartMapRequestExtra) => {
            const index = items.value.findIndex((m: IShoppingCardItem) => m.type === 'PA' && m.origPaId === extra.mapRequestId);
            if (index > -1) {
                items.value.splice(index + 1, 0, createImklItem(extra, false));
            } else {
                items.value.push(createImklItem(extra, true));
            }
        });
    }

    const _setTotalAmount = (value: number) => {
        totalAmount.value = value;
    }

    const _setTotalPriceInclVatFormatted = (value: string) => {
        totalPriceInclVatFormatted.value = value;
    }

    const _setTotalPriceExclVatFormatted = (value: string) => {
        totalPriceExclVatFormatted.value = value;
    }

    const _setTotalVatFormatted = (value: string) => {
        totalVatFormatted.value = value;
    }

    const _setVatPercentage = (value: number | undefined) => {
        vatPercentage.value = value;
    }

    const _updateShoppingCartMutation = (newStatus: ShoppingCartStatus) => {
        status.value = newStatus.status;
        paymentLink.value = newStatus.paymentLink;
    }

    const setItemToBeRemoved = (value: IShoppingCardItem) => {
        itemToBeRemoved.value = value;
    }

    const updateShoppingCart = async () => {
        if (isShoppingCartActive.value) {
            const shoppingCartStatusResponse = await klipApiProxy.payment_GetShoppingCartStatus();
            _updateShoppingCartMutation(shoppingCartStatusResponse.result);
            if (shoppingCartStatusResponse.result.status === 'created' || shoppingCartStatusResponse.result.status === 'submitted') {
                try {
                    useErrorStore().setBypassError(['405']);
                    const shoppingCartViewResponse = await klipApiProxy.payment_GetShoppingCartView();
                    _setShoppingCartItems(shoppingCartViewResponse.result.mapRequests);

                    if (shoppingCartViewResponse.result.mapRequestExtras && shoppingCartViewResponse.result.mapRequestExtras.length > 0) {
                        _addExtraShoppingCartItems(shoppingCartViewResponse.result.mapRequestExtras);
                    }
                    _setTotalAmount(shoppingCartViewResponse.result.totalPriceExclVat);
                    _setTotalPriceInclVatFormatted(shoppingCartViewResponse.result.totalPriceInclVatFormatted);
                    _setTotalPriceExclVatFormatted(shoppingCartViewResponse.result.totalPriceExclVatFormatted);
                    _setTotalVatFormatted(shoppingCartViewResponse.result.totalVatFormatted);
                    _setVatPercentage(shoppingCartViewResponse.result.vatPercentage);
                } catch (error: any) {
                    if (error.response && error.response.status && error.response.status === 405) {
                        useUserStore().setShoppingCartInvisible();
                        _setShoppingCartItems(null);
                        _setTotalAmount(0);
                        useErrorStore().resetBypassError();
                    } else {
                        throw error;
                    }
                }
            }
        }
    }

    const removeItemFromShoppingCart = async () => {
        if (!!itemToBeRemoved.value) {
            let response;
            if (itemToBeRemoved.value.type === 'PA') {
                response = await klipApiProxy.payment_DeleteShoppingCartMapRequest(itemToBeRemoved.value.origPaId);
            } else {
                response = await klipApiProxy.payment_DeleteShoppingCartMapRequestExtra(itemToBeRemoved.value.origImklId);
            }
            if (response.statusCode === 200) {
                updateShoppingCart();
                setItemToBeRemoved(null);

                if (!isShoppingCartActive.value) {
                    router.push({ name: 'mri' });
                }
            }
        }
    }

    const removeShoppingCart = async () => {
        const response = await klipApiProxy.payment_DeleteShoppingCart();
        if (response.statusCode === 200) {
            updateShoppingCart();
            if (useUserStore().paymentType === KlipApi.PaymentTypes.Invoice) {
                useUserStore().setShoppingCartInvisible();
            }
            router.push({ name: 'mri' });
        }
    }

    return {
        numberOfItems,
        shoppingCartItems,
        shoppingCartTotalPriceExclVat,
        shoppingCartTotalPriceInclVat,
        shoppingCartTotalVat,
        shoppingVatPercentage,
        getTotalAmount,
        isShoppingCartActive,
        shoppingCartStatus,
        shoppingCartPaymentLink,
        numberOfMapRequests,
        hasMapRequestExtras,
        hasMapRequestsWithImkl,
        hasInvalidItems,
        setItemToBeRemoved,
        updateShoppingCart,
        removeItemFromShoppingCart,
        removeShoppingCart
    }
});
